// Hooks and utils
import cn from '@/utils/cn';

import { ColorThemeEnum } from '@/enum/ColorThemeEnum';
import { WaveWrapperModeEnum } from '@/enum/WaveWrapperModeEnum';
import { WaveWrapperType } from '@/types/WaveWrapperType';
import { getGradientByColor } from '@/utils/color';
import styles from './WaveWrapper.module.css';

interface WaveWrapperProps {
  children: React.ReactNode;
  /** Layout éditorial du bloc (ex. text-media), exposé en `data-layout`. */
  layout?: 'default' | 'reverse';
  variant?: WaveWrapperType['variant'];
  color?: WaveWrapperType['color'];
  mode?: WaveWrapperType['mode'];
  active?: boolean;
  className?: string;
  contentClassName?: string;
  blockName: string;
}

function WaveWrapper({
  children,
  layout = 'default',
  variant = 'regular',
  color = ColorThemeEnum.ORANGE,
  mode = WaveWrapperModeEnum.WRAPPED,
  active = true,
  className = '',
  contentClassName = '',
  blockName,
}: WaveWrapperProps) {
  const gradientTheme = getGradientByColor(color);
  const showWave = Boolean(active && gradientTheme);

  const waveBackground =
    showWave && gradientTheme ? `linear-gradient(-40deg, ${gradientTheme.start}, ${gradientTheme.end})` : undefined;

  const isBackgroundMode = mode === WaveWrapperModeEnum.BACKGROUND;

  return (
    <section
      className={cn([styles.maskContainer, `styles__${variant}`, className])}
      data-block-name={blockName}
      data-layout={layout}
      data-variant={variant}
      data-mode={mode}
      data-wave-active={active}
    >
      {isBackgroundMode ? (
        <div className={cn([styles.waveStack, styles.maskBox])}>
          {showWave ? (
            <div
              className={cn([styles.waveDecor, styles.maskBoxActive])}
              style={waveBackground ? { background: waveBackground } : undefined}
              data-mode={mode}
              aria-hidden
            />
          ) : null}
          <div className={cn([styles.content, showWave && styles.contentBackground, contentClassName])}>{children}</div>
        </div>
      ) : (
        <div
          className={cn([styles.maskBox, showWave && styles.maskBoxActive])}
          style={waveBackground ? { background: waveBackground } : undefined}
        >
          <div className={cn([styles.content, contentClassName])}>{children}</div>
        </div>
      )}
    </section>
  );
}

export default WaveWrapper;
